// Period utilities for the analytics dashboard.

export type PeriodKind = "quick" | "custom";

export interface Period {
	from: Date;
	to: Date;
	label: string;
	kind: PeriodKind;
}

export type QuickRange = "today" | "7d" | "30d" | "90d" | "12m" | "custom";

const dayMs = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date): Date => {
	const x = new Date(d);
	x.setUTCHours(0, 0, 0, 0);
	return x;
};

const endOfDayExclusive = (d: Date): Date => {
	const x = startOfDay(d);
	x.setUTCDate(x.getUTCDate() + 1);
	return x;
};

export const buildPeriod = (
	from: Date,
	to: Date,
	label: string,
	kind: PeriodKind = "quick"
): Period => ({ from, to, label, kind });

export const periodFromQuickRange = (
	range: QuickRange,
	now: Date = new Date()
): Period => {
	const today = endOfDayExclusive(now); // exclusive upper
	switch (range) {
		case "today": {
			const from = startOfDay(now);
			return buildPeriod(from, today, "Today");
		}
		case "7d": {
			const from = new Date(today.getTime() - 7 * dayMs);
			return buildPeriod(from, today, "Last 7 days");
		}
		case "30d": {
			const from = new Date(today.getTime() - 30 * dayMs);
			return buildPeriod(from, today, "Last 30 days");
		}
		case "90d": {
			const from = new Date(today.getTime() - 90 * dayMs);
			return buildPeriod(from, today, "Last 90 days");
		}
		case "12m": {
			const from = new Date(today);
			from.setUTCFullYear(from.getUTCFullYear() - 1);
			return buildPeriod(from, today, "Last 12 months");
		}
		default: {
			const from = new Date(today.getTime() - 30 * dayMs);
			return buildPeriod(from, today, "Custom", "custom");
		}
	}
};

/**
 * Display the half-open range [from, to) as a closed-looking calendar
 * range for human eyes. The API contract uses an exclusive `to`
 * (typically the next-day UTC midnight); rendering it verbatim makes
 * "April 1 → May 1" look like a 31-day range when the user actually
 * picked "April 1 → April 30". When `to` falls on a UTC midnight we
 * subtract one day for display so the printed end-date matches the
 * inclusive label the user expects.
 */
export const formatRange = (from: Date, to: Date): string => {
	const fmt = (d: Date) =>
		`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(
			2,
			"0"
		)}-${String(d.getUTCDate()).padStart(2, "0")}`;
	const isUtcMidnight =
		to.getUTCHours() === 0 &&
		to.getUTCMinutes() === 0 &&
		to.getUTCSeconds() === 0 &&
		to.getUTCMilliseconds() === 0;
	const displayTo = isUtcMidnight ? new Date(to.getTime() - dayMs) : to;
	return `${fmt(from)} → ${fmt(displayTo)}`;
};

export const toIso = (d: Date): string => d.toISOString();

export const toDateInputValue = (d: Date): string =>
	`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(
		2,
		"0"
	)}-${String(d.getUTCDate()).padStart(2, "0")}`;

/**
 * Strict YYYY-MM-DD parser. Returns `null` for empty / malformed
 * inputs so callers can reject them visibly rather than silently
 * falling back to 1970-01-01 (which historically allowed the user to
 * trigger a multi-decade query just by clearing a date field).
 */
export const fromDateInputValue = (
	v: string | null | undefined
): Date | null => {
	if (!v) return null;
	const parts = v.split("-");
	if (parts.length !== 3) return null;
	if (
		parts[0]!.length !== 4 ||
		parts[1]!.length !== 2 ||
		parts[2]!.length !== 2
	)
		return null;
	const y = parseInt(parts[0]!, 10);
	const mo = parseInt(parts[1]!, 10);
	const d = parseInt(parts[2]!, 10);
	if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d))
		return null;
	if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
	const dt = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0));
	// Reject calendar-impossible dates (e.g. 2026-02-31) that would
	// otherwise round-trip through Date by overflowing into the next month.
	if (
		dt.getUTCFullYear() !== y ||
		dt.getUTCMonth() !== mo - 1 ||
		dt.getUTCDate() !== d
	)
		return null;
	return dt;
};

export type GroupBy =
	| "hour"
	| "day"
	| "week"
	| "month"
	| "quarter"
	| "year"
	| "entityId"
	| "userId"
	| "os"
	| "deviceType"
	| "appVersion"
	| "orgTeamLevelOne"
	| "orgTeamLevelTwo"
	| "storeId"
	| string; // allow `property:<key>`

export const autoBucketFor = (period: Period): GroupBy => {
	const days = (period.to.getTime() - period.from.getTime()) / dayMs;
	if (days <= 90) return "day";
	if (days <= 366) return "week";
	if (days <= 36 * 30) return "month";
	return "quarter";
};

export const periodSpanDays = (period: Period): number =>
	Math.max(
		1,
		Math.round((period.to.getTime() - period.from.getTime()) / dayMs)
	);
